import { motion } from "framer-motion";
import { ArrowRight, Download, BadgeCheck } from "lucide-react";

const skills = [
  "React",
  "TypeScript",
  "Django",
  "Python",
  "Tailwind CSS",
  "REST API",
  "Framer Motion",
];

const marqueeSkills = [...skills, ...skills];

const projectStats = [
  { label: "Projects", value: "8+" },
  { label: "Experience", value: "1+ Yr" },
  { label: "Freelance", value: "Open" },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.65,
      delay,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

export default function Hero() {
  return (
    <section
      id="home"
      className="relative overflow-hidden bg-white text-gray-900 dark:bg-[#030712] dark:text-white"
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-[-8rem] top-[-8rem] h-[28rem] w-[28rem] rounded-full bg-emerald-200/40 blur-3xl dark:bg-emerald-500/10" />
        <div className="absolute right-[-8rem] top-24 h-[26rem] w-[26rem] rounded-full bg-sky-200/30 blur-3xl dark:bg-sky-500/10" />
        <div className="absolute bottom-[-10rem] left-1/2 h-[26rem] w-[26rem] -translate-x-1/2 rounded-full bg-slate-200/40 blur-3xl dark:bg-slate-500/10" />
      </div>

      <div className="mx-auto max-w-7xl px-6 py-8 lg:px-8 lg:py-12">
        <div className="grid items-center gap-14 lg:grid-cols-[1.05fr_0.95fr]">
          {/* LEFT */}
          <div className="max-w-3xl">
            <motion.div
              custom={0}
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              className="flex flex-wrap items-center gap-3"
            >
              <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-300">
                <BadgeCheck className="h-4 w-4" />
                Computer Engineering Graduate
              </span>
            </motion.div>

            <motion.h1
              custom={0.12}
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              className="mt-6 text-6xl font-bold tracking-tight sm:text-7xl lg:text-8xl"
            >
              Sujita Regmi
            </motion.h1>

            <motion.p
              custom={0.24}
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              className="mt-5 max-w-xl text-xl leading-9 text-gray-600 dark:text-gray-400"
            >
              Practical web interfaces with React and Django, built with
              clarity, clean structure, and a focus on usability.
            </motion.p>

            <motion.p
              custom={0.34}
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              className="mt-4 max-w-xl text-lg leading-8 text-gray-500 dark:text-gray-500"
            >
              Thoughtful layouts and simple interactions matter more than flashy
              effects.
            </motion.p>

            <motion.div
              custom={0.44}
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              className="mt-10 flex flex-wrap gap-4"
            >
              <a
                href="#projects"
                className="group inline-flex items-center gap-2 rounded-xl bg-gray-900 px-6 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-1 hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-200"
              >
                View Projects
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </a>

              <a
                href="/Resume.pdf"
                download
                className="inline-flex items-center gap-2 rounded-xl border border-gray-300 bg-white px-6 py-3.5 text-sm font-semibold text-gray-900 transition-all duration-300 hover:-translate-y-1 hover:bg-gray-100 dark:border-gray-700 dark:bg-transparent dark:text-white dark:hover:bg-gray-900"
              >
                <Download className="h-4 w-4" />
                Resume
              </a>
            </motion.div>
          </div>

          {/* RIGHT */}
          <motion.div
            custom={0.22}
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            className="flex justify-center lg:justify-end"
          >
            <div className="relative w-full max-w-md">
              <div className="absolute -inset-3 rounded-[2.2rem] bg-gradient-to-br from-emerald-400/20 via-transparent to-sky-400/20 blur-2xl dark:from-emerald-500/10 dark:to-sky-500/10" />

              <div className="relative rounded-[2rem] border border-gray-200 bg-white/80 p-4 shadow-2xl backdrop-blur-xl dark:border-gray-800 dark:bg-gray-900/70">
                <div className="overflow-hidden rounded-[1.5rem] bg-gray-100 dark:bg-gray-800">
                  <img
                    src="/Photo.png"
                    alt="Sujita Regmi"
                    className="h-[460px] w-full object-cover object-top"
                  />
                </div>

                <div className="mt-4 flex items-center justify-between gap-4 px-1">
                  <div>
                    <h3 className="text-2xl font-bold">Sujita Regmi</h3>
                    <p className="mt-1 text-emerald-600 dark:text-emerald-400">
                      Full-Stack Developer
                    </p>
                  </div>

                  <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-300">
                    <span className="h-2 w-2 rounded-full bg-emerald-500" />
                    Available
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-3 gap-2">
                  {projectStats.map((item) => (
                    <div
                      key={item.label}
                      className="rounded-2xl border border-gray-200 bg-gray-50 px-3 py-3 text-center dark:border-gray-800 dark:bg-gray-950/40"
                    >
                      <p className="text-lg font-bold text-gray-900 dark:text-white">
                        {item.value}
                      </p>
                      <p className="mt-1 text-[11px] uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">
                        {item.label}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* MARQUEE */}
        <div className="mt-16 relative left-1/2 w-screen -translate-x-1/2 border-y border-gray-200 bg-white/70 py-4 backdrop-blur dark:border-gray-800 dark:bg-gray-950/50">
          <div className="overflow-hidden">
            <motion.div
              animate={{ x: ["0%", "-50%"] }}
              transition={{
                duration: 20,
                repeat: Infinity,
                ease: "linear",
              }}
              className="flex w-max items-center gap-8 px-6 whitespace-nowrap"
            >
              {marqueeSkills.map((skill, index) => (
                <div
                  key={`${skill}-${index}`}
                  className="flex items-center gap-8"
                >
                  <span className="text-sm font-medium tracking-wide text-gray-700 dark:text-gray-300">
                    {skill}
                  </span>
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500/80" />
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
